import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Clock, Sun, Moon } from "lucide-react";

/**
 * Visar dagens (eller morgondagens) skoltider per barn.
 * Format: "Anton 08:15–15:25"
 *
 * Rubrik-switch:
 *  - Kl. 00:00 → 17:59: "SKOLTIDER IDAG" (visar dagens schema)
 *  - Kl. 18:00 → 23:59: "SKOLTIDER I MORGON" (visar morgondagens schema)
 *
 * Detta gör att rubriken alltid är relevant — vid läggdags (när dagens skola är
 * över) ser man redan vad imorgon innebär istället för en helt tom kort.
 */
export function SchoolHoursCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTomorrow, setIsTomorrow] = useState(false);

  // Slot-baserad bucket — byter värde EXAKT när vi passerar 18:00 eller 00:00.
  // Används som useEffect-dep så vi tvingar refetch vid övergångarna utan att
  // behöva tunga timers eller fullständig page reload.
  const [slotKey, setSlotKey] = useState(() => {
    const n = new Date();
    return `${n.toISOString().slice(0,10)}-${n.getHours() >= 18 ? "evening" : "day"}`;
  });

  // Tickar varje minut och uppdaterar slot-nyckeln när vi passerar gränserna.
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      const next = `${n.toISOString().slice(0,10)}-${n.getHours() >= 18 ? "evening" : "day"}`;
      setSlotKey((prev) => (prev !== next ? next : prev));
    };
    const id = setInterval(tick, 60_000);
    // Säkerställ också uppdatering när användaren kommer tillbaka till fliken
    // efter en längre paus (mobil-PWA suspenderar setInterval i bakgrunden).
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  useEffect(() => {
    // Beräkna i lokal tid (browser = Europe/Stockholm för familjen)
    const now = new Date();
    const showTomorrow = now.getHours() >= 18;
    setIsTomorrow(showTomorrow);
    let targetDate;
    if (showTomorrow) {
      const t = new Date(now);
      t.setDate(t.getDate() + 1);
      targetDate = t.toISOString().slice(0, 10);
    }
    let alive = true;
    setLoading(true);
    const url = targetDate ? `/school-hours/today?date=${targetDate}` : "/school-hours/today";
    api.get(url)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ children: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slotKey]);

  // Inget visas under helg/lov (eller om backend inte hittade lektioner)
  if (!loading && (!data || !data.children || data.children.length === 0)) {
    return null;
  }

  return (
    <div
      className="card-soft p-3 mb-4 fade-up"
      data-testid="school-hours-card"
    >
      <div className="flex items-center gap-1.5 mb-2">
        {isTomorrow ? (
          <Moon size={12} className="text-[#3D5A80]" />
        ) : (
          <Sun size={12} className="text-[#E07A5F]" />
        )}
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">
          {isTomorrow ? "Skoltider i morgon" : "Skoltider idag"}
        </p>
      </div>
      {loading ? (
        <p className="text-xs text-[color:var(--text-tertiary)]">Laddar…</p>
      ) : (
        <ul className="space-y-1.5">
          {data.children.map((c) => (
            <li
              key={c.child}
              className="flex items-center gap-2 text-sm"
              data-testid={`school-hours-${c.child}`}
            >
              <span className="font-semibold text-[color:var(--text-primary)] min-w-[70px]">
                {c.child}
              </span>
              <span className="flex items-center gap-1 text-[color:var(--text-primary)] tabular-nums font-medium">
                <Clock size={11} className="text-[color:var(--text-tertiary)]" />
                {c.start}–{c.end}
              </span>
              <span className="text-[11px] text-[color:var(--text-tertiary)] ml-auto">
                {c.lesson_count} lektioner
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
